import { AgDialog } from '@australiangreens/ag-internal-components';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { useCurrentSearchTree } from './CurrentSearchTreeContext';
import { shouldConfirmDeleteForNode } from './util';

export type DeleteActionType = 'delRule' | 'delGroup' | 'delRuleGroup';

export type PendingDeleteTarget = {
  actionType: DeleteActionType;
  nodeId?: string;
} | null;

export type ConfirmContextValue = () => Promise<boolean>;

export type ConfirmDeleteDialogConfig = {
  title: string;
  description: string | ReactNode;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

type SetPendingDeleteTargetFn = (target: PendingDeleteTarget) => void;
const SetPendingDeleteTargetContext = createContext<SetPendingDeleteTargetFn | null>(null);

type ClearPendingDeleteTargetFn = () => void;
const ClearPendingDeleteTargetContext = createContext<ClearPendingDeleteTargetFn | null>(null);

type ConfirmState = {
  resolve: (result: boolean) => void;
} | null;

type ConfirmProviderProps = {
  children: ReactNode;
};

/**
 * This provider is used in conjunction with the query builder config. Whenever
 * a rule or group is deleted, the query builder config will call the function
 * returned by useConfirmDeleteRuleOrGroup.
 *
 * Whether the dialog is shown is determined by the pending delete target and
 * whether the target node/subtree contains placeholder-backed filters. The
 * DeletionInterceptionButton sets the pending target on delete-button clicks
 * before forwarding the click.
 */
export const ConfirmDeleteRuleOrGroupProvider = ({ children }: ConfirmProviderProps) => {
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [dialogConfig, setDialogConfig] = useState<ConfirmDeleteDialogConfig>({
    title: 'Remove item',
    description: '',
  });

  const pendingDeleteTargetRef = useRef<PendingDeleteTarget>(null);
  const currentSearchTree = useCurrentSearchTree();

  const isConfirmDialogOpen = confirmState !== null;

  const setPendingDeleteTarget = useCallback((target: PendingDeleteTarget) => {
    pendingDeleteTargetRef.current = target;
  }, []);

  const clearPendingDeleteTarget = useCallback(() => {
    pendingDeleteTargetRef.current = null;
  }, []);

  const confirm: ConfirmContextValue = useCallback(() => {
    const target = pendingDeleteTargetRef.current;

    if (!target) {
      return Promise.resolve(true);
    }

    if (!target.nodeId) {
      return Promise.resolve(true);
    }

    const result = shouldConfirmDeleteForNode(currentSearchTree ?? undefined, target.nodeId);

    if (!result.shouldConfirm) {
      pendingDeleteTargetRef.current = null;
      return Promise.resolve(true);
    }

    setDialogConfig({
      title: `Remove ${result.kind}`,
      description: (
        <>
          {`This ${result.kind} has one or more fields that have been set as placeholders. Are you sure you want to remove the ${result.kind}?`}
          <br />
          <br />
          <em>Note: any placeholder instructions will also be lost.</em>
        </>
      ),
    });

    return new Promise<boolean>((resolve) => {
      setConfirmState({ resolve });
    });
  }, [currentSearchTree]);

  const handleClose = useCallback((result: boolean) => {
    pendingDeleteTargetRef.current = null;
    setConfirmState((prev: ConfirmState) => {
      if (prev) {
        prev.resolve(result);
      }
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      <SetPendingDeleteTargetContext.Provider value={setPendingDeleteTarget}>
        <ClearPendingDeleteTargetContext.Provider value={clearPendingDeleteTarget}>
          {children}

          <AgDialog
            dialogTitle={dialogConfig.title}
            isOpen={isConfirmDialogOpen}
            primaryButton={{
              text: 'Remove',
              onClick: () => Promise.resolve(handleClose(true)),
            }}
            onClose={() => handleClose(false)}
            secondaryButton={{ text: 'Cancel', onClick: () => Promise.resolve(handleClose(false)) }}
          >
            {dialogConfig.description}
          </AgDialog>
        </ClearPendingDeleteTargetContext.Provider>
      </SetPendingDeleteTargetContext.Provider>
    </ConfirmContext.Provider>
  );
};

export const useConfirmDeleteRuleOrGroup = () => useContext(ConfirmContext);

export const useSetPendingDeleteTarget = () => useContext(SetPendingDeleteTargetContext);

export const useClearPendingDeleteTarget = () => useContext(ClearPendingDeleteTargetContext);
